/**
 * DefaultMobileBanner Component
 * Default mobile banner that appears on all mobile pages automatically
 * Shows hamburger menu, page title, and star coins
 * Terminal glassmorphism design system
 */

import React, { useState } from 'react';
import { Menu } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import StarCoinsDisplay from '../starCoinSystem/StarCoinsDisplay';
import { getBrazilianStatus } from '../../utils/detectBrazil';
import { defaultMobileBannerPtBR } from '../mappings/defaultMobileBanner.pt-br';

const DefaultMobileBanner = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuHovered, setIsMenuHovered] = useState(false);
  const isBrazilian = getBrazilianStatus();

  // Get page name from route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 'PICKSTAR';
    if (path === '/sports-hub') return 'SPORTS';
    if (path === '/market') return 'MARKET';
    if (path === '/chat') return 'CHAT';
    if (path === '/upgrade') return 'UPGRADE';
    if (path.includes('/admin')) return 'ADMIN';
    if (path.includes('/nba')) return 'NBA';
    if (path.includes('/nfl')) return 'NFL';
    if (path.includes('/mlb')) return 'MLB';
    if (path.includes('/mma')) return 'MMA';

    // Default: extract from path
    const pageName = path.split('/').filter(Boolean)[0] || 'PICKSTAR';
    return pageName.toUpperCase().slice(0, 10); // Limit length
  };

  // Translation helper using mapping file
  const t = (text) => {
    if (!isBrazilian) return text;
    return defaultMobileBannerPtBR[text] || text;
  };

  // Handle hamburger menu click
  const handleMenuClick = () => {
    if (typeof window.toggleMobileMenu === 'function') {
      window.toggleMobileMenu();
    }
  };

  return (
    <div className="w-full bg-black/90 backdrop-blur-lg border-b border-white/[0.05]">
      <div className="px-3 py-2.5">
        <div className="flex items-center justify-between">

          {/* Left Section: Hamburger Menu + Page Title */}
          <div className="flex items-center gap-3">
            {/* Hamburger Menu Button */}
            <button
              onClick={handleMenuClick}
              onMouseEnter={() => setIsMenuHovered(true)}
              onMouseLeave={() => setIsMenuHovered(false)}
              className="p-2 rounded-lg bg-white/[0.02] hover:bg-white/[0.05]
                         border border-white/[0.03] hover:border-white/[0.08]
                         transition-all duration-200 active:scale-95"
              aria-label="Menu"
            >
              <Menu className={`w-5 h-5 text-white/60 hover:text-white/80
                              transition-all duration-200 ${isMenuHovered ? 'scale-110' : ''}`} />
            </button>

            <h1
              onClick={() => navigate('/home')}
              className="text-[11px] font-mono font-medium tracking-[0.25em]
                         text-white/40 uppercase cursor-pointer
                         hover:text-white/60 transition-all select-none"
            >
              {t(getPageTitle())}
            </h1>
          </div>

          {/* Center spacer */}
          <div className="flex-1"></div>

          {/* Right Section: Star Coins Display */}
          <div className="flex items-center">
            {user ? (
              <StarCoinsDisplay
                userId={user.uid}
                isMobile={true}
                compact={true}
                customStyles={{
                  container: 'bg-white/[0.02] border-white/[0.05]',
                  text: 'text-white/50',
                  icon: 'text-emerald-400/60'
                }}
              />
            ) : (
              // Login prompt for non-authenticated users
              <button
                onClick={() => navigate('/login')}
                className="px-3 py-1.5 rounded-lg bg-white/[0.02] border border-white/[0.05]
                           hover:bg-white/[0.05] hover:border-white/[0.08]
                           transition-all duration-200 active:scale-95"
              >
                <span className="font-mono text-[9px] text-white/40 uppercase tracking-wider">
                  {t('Login')}
                </span>
              </button>
            )}
          </div>

        </div>
      </div>
    </div>
  );
};

export default DefaultMobileBanner;